import { clsx } from 'clsx';
import { useState } from 'react';

import type { ConnectedFacility } from '../../../../../js/api';
import { SourceTypeEnum } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { DrawerField } from '../../../components/drawer-components';
import { FacilityDetails } from '../../../components/FacilityDetails';
import { StatusCodes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { formatDateTo24Hr } from '../../../utils/date';

type DetailsTabProps = {
  connection: ConnectedFacility;
};

const ORIGINAL_RATE_REASON_CODE = 'ORIGINAL';

type RateDisplayProps = {
  currentRate: number | null;
  originalRate: number | null | undefined;
  label: string;
  hasChange: boolean;
  formatCurrencyPostfix: (value: number | null | undefined) => string;
};

function RateDisplay({
  currentRate,
  originalRate,
  label,
  hasChange,
  formatCurrencyPostfix,
}: RateDisplayProps) {
  if (!currentRate || currentRate === 0) return null;

  return (
    <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
      {hasChange ? (
        <div className="tw:flex tw:flex-col tw:gap-1">
          <div className="tw:flex tw:items-center tw:gap-2">
            <span className="tw:line-through tw:text-zinc-400 tw:text-sm">
              {formatCurrencyPostfix(originalRate)}
            </span>
            <span
              className={
                currentRate > originalRate!
                  ? 'tw:text-green-600 dark:tw:text-green-400'
                  : 'tw:text-red-600 dark:tw:text-red-400'
              }
            >
              {formatCurrencyPostfix(currentRate)}
            </span>
          </div>
          <div className="tw:text-xs tw:italic tw:text-zinc-500">{label}</div>
        </div>
      ) : (
        <>
          {formatCurrencyPostfix(currentRate)}
          <div className="tw:text-xs tw:italic tw:text-zinc-500">{label}</div>
        </>
      )}
    </div>
  );
}

export function DetailsTab({ connection }: DetailsTabProps) {
  const [showFacilityDetails, setShowFacilityDetails] = useState(
    connection.current_status?.code === StatusCodes.REQUESTED
  );
  const opportunity = connection.opportunity;
  const facilityId = connection.facility?.id;

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps(['FacilityTypes', 'Specialty']);
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });

  // Get provider rates
  const dayRate = connection.shift_rate_day;
  const nightRate = connection.shift_rate_night;
  const rate24h = connection.shift_rate_24h;
  const callRate = connection.shift_rate_call;

  // Get original rates from rate_history
  const originalRateData = connection.rate_history?.find(
    (rate) => rate.reason_code === ORIGINAL_RATE_REASON_CODE
  );

  const originalRates = {
    dayRate: originalRateData?.shift_rate_day ?? null,
    nightRate: originalRateData?.shift_rate_night ?? null,
    rate24h: originalRateData?.shift_rate_24h ?? null,
    callRate: originalRateData?.shift_rate_call ?? null,
  };

  // Show rate comparison for all statuses except ACTIVE
  const showRateComparison = connection.current_status?.code !== StatusCodes.ACTIVE;

  // Helper to check if rate has changed
  const hasRateChange = (
    currentRate: number | null,
    originalRate: number | null | undefined
  ): boolean => {
    return (
      showRateComparison &&
      originalRate !== undefined &&
      originalRate !== null &&
      currentRate !== null &&
      originalRate !== currentRate
    );
  };

  return (
    <div className="tw:space-y-4">
      {/* Rate Information */}
      <div className="tw:bg-brand-green-3 tw:rounded-md tw:mt-3 tw:p-2">
        <div className="tw:text-sm tw:font-medium tw:mb-1 tw:flex tw:justify-between tw:items-center">
          Hourly Rates
          {connection.source_type === SourceTypeEnum.ORGANIZATION && (
            <span
              className={clsx(
                'tw:flex tw:items-center tw:gap-1',
                SOURCE_TYPE_ORGANIZATION_BADGE_STYLE
              )}
            >
              1099
            </span>
          )}
        </div>
        <div className="tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-1">
          <RateDisplay
            currentRate={dayRate}
            originalRate={originalRates.dayRate}
            label="Daytime"
            hasChange={hasRateChange(dayRate, originalRates.dayRate)}
            formatCurrencyPostfix={formatCurrencyPostfix}
          />
          <RateDisplay
            currentRate={nightRate}
            originalRate={originalRates.nightRate}
            label="Overnight"
            hasChange={hasRateChange(nightRate, originalRates.nightRate)}
            formatCurrencyPostfix={formatCurrencyPostfix}
          />
          <RateDisplay
            currentRate={rate24h}
            originalRate={originalRates.rate24h}
            label="24 hour"
            hasChange={hasRateChange(rate24h, originalRates.rate24h)}
            formatCurrencyPostfix={formatCurrencyPostfix}
          />
          <RateDisplay
            currentRate={callRate}
            originalRate={originalRates.callRate}
            label="Call"
            hasChange={hasRateChange(callRate, originalRates.callRate)}
            formatCurrencyPostfix={formatCurrencyPostfix}
          />
        </div>
      </div>

      <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
        {/* Additional Terms & Expected Expenses */}
        {connection.additional_terms && (
          <DrawerField disableIndent label="Additional Terms" value={connection.additional_terms} />
        )}
        {connection.expected_expenses && connection.expected_expenses.length > 0 && (
          <DrawerField
            disableIndent
            label="Expected Expenses"
            value={connection.expected_expenses
              .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
              .join(', ')}
          />
        )}

        {/* Dates */}
        <DrawerField
          disableIndent
          label="Created"
          value={
            connection.connected_at
              ? formatDateTo24Hr(connection.connected_at, 'MM/dd/yyyy HH:mm')
              : null
          }
        />
        <DrawerField
          disableIndent
          label="Last Updated"
          value={
            connection.updated_at
              ? formatDateTo24Hr(connection.updated_at, 'MM/dd/yyyy HH:mm')
              : null
          }
        />
      </div>

      {/* Opportunity Details */}
      {facilityId && (
        <div className="tw:mt-4">
          <button
            type="button"
            onClick={() => setShowFacilityDetails(!showFacilityDetails)}
            className="tw:p-0 tw:text-decoration-none tw:flex tw:items-center tw:gap-2 tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0 tw:bg-transparent tw:border-0 tw:cursor-pointer"
          >
            {showFacilityDetails ? (
              <i className="fas fa-caret-down" />
            ) : (
              <i className="fas fa-caret-right" />
            )}
            <span>{showFacilityDetails ? 'Hide' : 'Show'} More Details</span>
          </button>

          <FacilityDetails
            showDetails={showFacilityDetails}
            opportunity={opportunity}
            facilityId={facilityId}
            codeDefinitions={codeDefinitionMaps}
          />
        </div>
      )}
    </div>
  );
}
